import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconCode,
  IconDatabase,
  IconInfoCircle,
  IconListDetails,
  IconMail,
  IconServer,
  IconSettings,
  IconTemplate,
  IconVariable,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const Emails = () => {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Email Documentation"
        subtitle="Complete reference for the email template and delivery system"
        showBreadcrumbs={false}
      />

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#templates">Email Templates</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#trigger-events">Trigger Events</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#variables">Template Variables</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#configuration">SMTP Configuration</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#email-field">Email Field Resolution</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconMail size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The email system allows organizations to manage email templates and
            SMTP server configuration for sending transactional emails to users.
            Emails are triggered by specific platform events (e.g. event
            registration, payment status changes) and are populated with dynamic
            variables.
          </Text>
          <Text mb="md">The system is composed of three main parts:</Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Email System
  +-- SMTP Configuration (server connection, from/reply-to addresses)
  +-- Email Field Resolution (how to find each user's email address)
  +-- Email Templates[]
       +-- Trigger Event (which platform event fires this email)
       +-- Title (email subject line, supports variables)
       +-- Contents (HTML body, supports variables)
       +-- Default flag (one default template per trigger event)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            Each trigger event can have multiple templates, but only the one
            marked as <Code>default</Code> will be sent automatically when the
            event fires. Non-default templates can be sent manually via the{" "}
            <Code>emailSend</Code> mutation.
          </Alert>
        </div>

        {/* ===================== TEMPLATES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTemplate size={18} />}
            title="Email Templates"
            id="templates"
          />
          <Text mb="md">
            Email templates define the subject line and HTML body content for
            emails. Each template is associated with a specific trigger event
            and has access to a set of variables determined by that event.
          </Text>

          <Title order={4} mb="xs">
            Creating a Template
          </Title>
          <Text mb="sm">
            Navigate to <Code>/emails/templates/new</Code>. Required fields:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Name</strong> - Internal name to identify the template
              (string, required)
            </List.Item>
            <List.Item>
              <strong>Trigger Event</strong> - The platform event that fires
              this email (select, required)
            </List.Item>
            <List.Item>
              <strong>Title</strong> - Email subject line, supports{" "}
              <Code>{"{{variable}}"}</Code> syntax (string, required)
            </List.Item>
            <List.Item>
              <strong>Default</strong> - Whether this is the default template
              for its trigger event (boolean, required)
            </List.Item>
            <List.Item>
              <strong>Contents</strong> - Full HTML email body with variable
              placeholders (code editor, required)
            </List.Item>
          </List>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mb="md"
          >
            When selecting a trigger event that has a built-in default template,
            the title and contents fields are automatically populated with a
            starter template. You can customize it or replace it entirely.
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Template List
          </Title>
          <Text mb="sm">
            The templates list page (<Code>/emails/templates</Code>) shows all
            configured templates with:
          </Text>
          <List spacing="xs" size="sm">
            <List.Item>Template name (clickable link to edit)</List.Item>
            <List.Item>
              Trigger event name (resolved from configuration)
            </List.Item>
            <List.Item>
              <Badge size="sm">Default</Badge> badge for default templates
            </List.Item>
            <List.Item>Delete action per row</List.Item>
          </List>
        </div>

        {/* ===================== TRIGGER EVENTS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Trigger Events"
            id="trigger-events"
          />
          <Text mb="md">
            Trigger events define when an email is sent. Each trigger event
            provides a specific set of variables that can be used in the
            template. The available trigger events are fetched from the API via
            the <Code>emailTemplateConfigurations</Code> query.
          </Text>

          <Title order={4} mb="xs">
            Available Trigger Events
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Event</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>EVENT_REGISTRATION_COMPLETED</Code>
                </td>
                <td>
                  Fired when a user completes event registration. Typically used
                  to send ticket confirmations with QR codes.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_PENDING</Code>
                </td>
                <td>
                  Fired when an admin sets a registration to pending/payment
                  status. Used to notify users that a payment session has
                  started.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_CONFIRMED</Code>
                </td>
                <td>Fired when an admin confirms a registration.</td>
              </tr>
              <tr>
                <td>
                  <Code>EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_DENIED</Code>
                </td>
                <td>Fired when an admin denies a registration.</td>
              </tr>
              <tr>
                <td>
                  <Code>EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_NONE</Code>
                </td>
                <td>
                  Fired when an admin resets a registration status to none.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>
                    EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_NOT_ATTENDING
                  </Code>
                </td>
                <td>
                  Fired when an admin marks a registration as not attending.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>
                    EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_PRESENCE_CONFIRMATION_PENDING
                  </Code>
                </td>
                <td>
                  Fired when an admin sets a registration to pending presence
                  confirmation.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_REGISTERED</Code>
                </td>
                <td>
                  Fired when an admin sets a registration back to registered
                  status.
                </td>
              </tr>
              <tr>
                <td>
                  <Code>
                    EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_REQUIRES_ADMIN_APPROVAL
                  </Code>
                </td>
                <td>Fired when a registration requires admin approval.</td>
              </tr>
              <tr>
                <td>
                  <Code>
                    EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_SESSION_EXPIRED
                  </Code>
                </td>
                <td>
                  Fired when a payment or registration session has expired.
                </td>
              </tr>
            </tbody>
          </Table>

          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            The list of trigger events is dynamic and fetched from the API. New
            trigger events may be added as new features are released. The table
            above reflects the currently known events.
          </Alert>
        </div>

        {/* ===================== VARIABLES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconVariable size={18} />}
            title="Template Variables"
            id="variables"
          />
          <Text mb="md">
            Template variables allow dynamic content injection into email
            subjects and bodies. Variables use the{" "}
            <Code>{"{{variableName}}"}</Code> syntax and are replaced with
            actual values when the email is sent.
          </Text>

          <Title order={4} mb="xs">
            Variable Syntax
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`<!-- In the email subject -->
Your tickets for {{event_name}} are ready!

<!-- In the email body -->
<p>Hi {{username}},</p>
<p>Your reservation for {{event_name}} is confirmed.</p>
<img src="{{qrcode_data_url}}" alt="QR Code" />`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            How Variables Are Resolved
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              Each trigger event defines its own set of available variables
              (fetched via <Code>emailTemplateConfigurations</Code>)
            </List.Item>
            <List.Item>
              When editing a template, the available variables for the selected
              trigger event are displayed below the code editor
            </List.Item>
            <List.Item>
              Variables that don't match any available variable name are left
              as-is in the output
            </List.Item>
          </List>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Variable availability depends on the trigger event. For example,{" "}
            <Code>{"{{qrcode_data_url}}"}</Code> is only available for event
            registration events. Always check the available variables list when
            creating a template for a new trigger event.
          </Alert>
        </div>

        {/* ===================== CONFIGURATION ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconServer size={18} />}
            title="SMTP Configuration"
            id="configuration"
          />
          <Text mb="md">
            The SMTP configuration defines the email server connection and
            sender identity. Navigate to <Code>/emails/configuration</Code> to
            configure these settings. Saving the configuration also triggers an
            SMTP connection test.
          </Text>

          <Title order={4} mb="xs">
            Connection Settings
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>host</Code>
                </td>
                <td>String!</td>
                <td>SMTP server hostname (e.g. smtp.gmail.com)</td>
              </tr>
              <tr>
                <td>
                  <Code>port</Code>
                </td>
                <td>Number</td>
                <td>SMTP server port (e.g. 587 for TLS, 465 for SSL)</td>
              </tr>
              <tr>
                <td>
                  <Code>username</Code>
                </td>
                <td>String</td>
                <td>SMTP authentication username</td>
              </tr>
              <tr>
                <td>
                  <Code>password</Code>
                </td>
                <td>String</td>
                <td>SMTP authentication password</td>
              </tr>
              <tr>
                <td>
                  <Code>secure</Code>
                </td>
                <td>Boolean!</td>
                <td>Whether to use a secure TLS/SSL connection</td>
              </tr>
            </tbody>
          </Table>

          <Title order={4} mb="xs">
            Sender Settings
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>from</Code>
                </td>
                <td>String</td>
                <td>Sender email address (From header)</td>
              </tr>
              <tr>
                <td>
                  <Code>fromName</Code>
                </td>
                <td>String</td>
                <td>Sender display name</td>
              </tr>
              <tr>
                <td>
                  <Code>replyTo</Code>
                </td>
                <td>String</td>
                <td>Reply-to email address</td>
              </tr>
              <tr>
                <td>
                  <Code>replyToName</Code>
                </td>
                <td>String</td>
                <td>Reply-to display name</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== EMAIL FIELD ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Email Field Resolution"
            id="email-field"
          />
          <Text mb="md">
            The email field configuration determines how the system finds each
            user's email address. This is configured as part of the SMTP
            configuration and supports two property sources.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Player Property Source
              </Text>
              <Text size="sm" mb="xs">
                Reads the email from a player profile property.
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Code>propertySource</Code>: <Code>PLAYER</Code>
                </List.Item>
                <List.Item>
                  <Code>property</Code>: The player profile field name
                  containing the email (e.g. "email")
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Identity Provider Source
              </Text>
              <Text size="sm" mb="xs">
                Reads the email from an identity provider's mapped data.
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Code>propertySource</Code>: <Code>IDENTITY_PROVIDER</Code>
                </List.Item>
                <List.Item>
                  <Code>propertySourceId</Code>: The ID of the identity provider
                </List.Item>
                <List.Item>
                  <Code>property</Code>: The mapped property name from the IDP
                  data retrievers
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            If the email field is not configured or resolves to an empty value
            for a user, emails will not be sent to that user. Ensure the
            property source and property name match your player data model.
          </Alert>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations available for the email
            system.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Email Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      emailTemplateConfigurations
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch all available trigger events and their configuration
                      (available variables, descriptions). Used to populate the
                      trigger event dropdown when creating/editing a template.
                    </Text>
                    <GqlBlock>
                      {`query {
  emailTemplateConfigurations {
    name
    description
    triggerEvent
    availableVariables {
      name
      description
    }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      emailTemplates (list)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Paginated list of all email templates for the
                      organization.
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float) {
  emailTemplates(page: { after: $cursor, first: $count }) {
    nodes {
      id
      name
      triggerEvent
      default
    }
    totalCount
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      emailTemplate (detail)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch a single email template by ID with full content.
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  emailTemplate(id: $id) {
    id
    name
    contents
    title
    triggerEvent
    default
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      emailConfiguration
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch the current SMTP and email field configuration.
                    </Text>
                    <GqlBlock>
                      {`query {
  emailConfiguration {
    host
    port
    username
    password
    from
    fromName
    replyTo
    replyToName
    secure
    emailField {
      property
      propertySource
      propertySourceId
    }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Email Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      emailTemplateCreate
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: EmailTemplateCreateInput!) {
  emailTemplateCreate(input: $input) {
    id
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      emailTemplateUpdate
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: EmailTemplateUpdateInput!) {
  emailTemplateUpdate(id: $id, input: $input) {
    id
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      emailTemplateDelete
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  emailTemplateDelete(id: $id) {
    id
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      emailConfigurationUpdate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Update SMTP configuration. Also tests the SMTP connection
                      on save.
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: EmailConfigurationInput!) {
  emailConfigurationUpdate(input: $input) {
    from
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="send-email">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Send Email</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Alert
                  icon={<IconInfoCircle />}
                  color="indigo"
                  variant="light"
                  mb="md"
                >
                  This mutation is available via the{" "}
                  <Anchor href="/emails/send">Send Email</Anchor> page in the
                  console, as well as programmatically from external
                  integrations or platform pages.
                </Alert>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      emailSend (from template)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Send an email using an existing template. The system
                      resolves the default template for the given trigger event
                      and populates it with the provided variables.
                    </Text>
                    <GqlBlock>
                      {`mutation ($fromTemplate: EmailSendFromTemplateInput) {
  emailSend(fromTemplate: $fromTemplate)
  # Returns: Boolean!
}

# EmailSendFromTemplateInput:
# {
#   triggerEvent: EmailTriggerEvents!
#   variables: [PropertyValueInput!]!
#   playerIds: ID         # Optional: target specific player
# }`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      emailSend (from custom)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Send a fully custom email without using a template.
                      Provide the subject and HTML body directly. Requires
                      specifying the email field configuration to resolve
                      recipient addresses.
                    </Text>
                    <GqlBlock>
                      {`mutation ($fromCustom: EmailSendFromCustomInput) {
  emailSend(fromCustom: $fromCustom)
  # Returns: Boolean!
}

# EmailSendFromCustomInput:
# {
#   subject: String!
#   body: String!
#   emailField: PlayerFieldInput!
#   playerIds: ID         # Optional: target specific player
# }`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Key types and enums used by the email system. All types are
            generated from the GraphQL schema.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="types">
              <Accordion.Control>Types</Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      EmailTemplate
                    </Text>
                    <GqlBlock>
                      {`type EmailTemplate {
  id: ID!
  name: String!
  title: String!
  contents: String!
  triggerEvent: EmailTriggerEvents!
  default: Boolean!
  organizationId: ID!
  createdAt: DateTime!
  updatedAt: DateTime!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      EmailTemplateConfiguration
                    </Text>
                    <GqlBlock>
                      {`type EmailTemplateConfiguration {
  name: String!
  description: String!
  triggerEvent: EmailTriggerEvents!
  availableVariables: [EmailTemplateConfigurationAvailableVariables!]!
}

type EmailTemplateConfigurationAvailableVariables {
  name: String!
  description: String!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      EmailConfiguration
                    </Text>
                    <GqlBlock>
                      {`type EmailConfiguration {
  host: String!
  port: Float
  username: String
  password: String
  from: String!
  fromName: String
  replyTo: String
  replyToName: String
  secure: Boolean!
  emailField: PlayerField!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="inputs">
              <Accordion.Control>Input Types</Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      EmailTemplateCreateInput
                    </Text>
                    <GqlBlock>
                      {`input EmailTemplateCreateInput {
  name: String!
  title: String!
  contents: String!
  triggerEvent: EmailTriggerEvents!
  default: Boolean!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      EmailTemplateUpdateInput
                    </Text>
                    <GqlBlock>
                      {`input EmailTemplateUpdateInput {
  name: String
  title: String
  contents: String
  triggerEvent: EmailTriggerEvents
  default: Boolean
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      EmailConfigurationInput
                    </Text>
                    <GqlBlock>
                      {`input EmailConfigurationInput {
  host: String!
  port: Float
  username: String
  password: String
  from: String!
  fromName: String
  replyTo: String
  replyToName: String
  secure: Boolean!
  emailField: PlayerFieldInput!
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      EmailSendFromTemplateInput / EmailSendFromCustomInput
                    </Text>
                    <GqlBlock>
                      {`input EmailSendFromTemplateInput {
  triggerEvent: EmailTriggerEvents!
  variables: [PropertyValueInput!]!
  playerIds: ID
}

input EmailSendFromCustomInput {
  subject: String!
  body: String!
  emailField: PlayerFieldInput!
  playerIds: ID
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <div>
                  <Text fw={600} size="sm" mb="xs">
                    EmailTriggerEvents
                  </Text>
                  <GqlBlock>
                    {`EVENT_REGISTRATION_COMPLETED
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_CONFIRMED
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_DENIED
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_NONE
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_NOT_ATTENDING
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_PENDING
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_PRESENCE_CONFIRMATION_PENDING
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_REGISTERED
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_REQUIRES_ADMIN_APPROVAL
EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_SESSION_EXPIRED`}
                  </GqlBlock>
                </div>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>
      </Stack>
    </div>
  );
};
